import { ApiError } from "@/common/lib/api";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import type { LotStage } from "@/features/animals/lifecycleStates";
import { useEffect, useState } from "react";
import { useCreateLot, useUpdateLot } from "../api/lotsApi";
import type { Lot } from "../types";
import { LotStageSelect } from "./LotStageSelect";

type Props = {
  accountId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: "create" | "edit";
  lot?: Lot;
  onSuccess?: () => void;
};

export function LotFormDialog({ accountId, open, onOpenChange, mode, lot, onSuccess }: Props) {
  const [name, setName] = useState("");
  const [stage, setStage] = useState<LotStage | "">("");
  const [isActive, setIsActive] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const createMut = useCreateLot(accountId);
  const updateMut = useUpdateLot(accountId);
  const pending = createMut.isPending || updateMut.isPending;

  useEffect(() => {
    if (!open) return;
    if (mode === "edit" && lot) {
      setName(lot.name);
      setStage(lot.stage);
      setIsActive(lot.isActive);
    } else {
      setName("");
      setStage("");
      setIsActive(true);
    }
    setError(null);
  }, [open, mode, lot]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    const trimmed = name.trim();
    if (!trimmed) {
      setError("El nombre es obligatorio.");
      return;
    }
    if (!stage) {
      setError("Selecciona una etapa.");
      return;
    }
    try {
      if (mode === "create") {
        await createMut.mutateAsync({ name: trimmed, stage });
      } else if (lot) {
        await updateMut.mutateAsync({
          lotId: lot.id,
          input: { name: trimmed, stage, isActive },
        });
      }
      onOpenChange(false);
      onSuccess?.();
    } catch (e) {
      if (e instanceof ApiError && e.code === "LOT_NAME_EXISTS") {
        // B105: mapear por code a español; nunca renderizar el e.message del
        // backend (que viene en inglés). Espejo de RegisterAnimalPage.
        setError("Ya existe un lote con ese nombre.");
      } else {
        setError("Ocurrió un error inesperado.");
      }
    }
  }

  const title = mode === "create" ? "Crear lote" : "Editar lote";
  const submitLabel = mode === "create" ? "Crear" : "Guardar";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>
            {mode === "create"
              ? "Los lotes agrupan animales por etapa para asignar costos."
              : "Cambios en nombre o etapa se aplican a los animales actualmente en el lote."}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <label htmlFor="lot-name" className="font-medium text-[13px] text-app-ink-2">
              Nombre
            </label>
            <input
              id="lot-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={200}
              disabled={pending}
              className="h-10 w-full rounded-lg border border-app-border bg-app-surface px-3 text-app-ink-1 text-base outline-none focus-visible:border-app-emerald focus-visible:ring-3 focus-visible:ring-app-emerald/20"
              placeholder="Ej. Vacas en producción"
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <label htmlFor="lot-stage" className="font-medium text-[13px] text-app-ink-2">
              Etapa
            </label>
            <LotStageSelect
              value={stage}
              onChange={setStage}
              ariaLabel="Etapa del lote"
              disabled={pending}
            />
          </div>

          {mode === "edit" && (
            <label className="flex items-center gap-2 text-app-ink-2 text-sm">
              <input
                type="checkbox"
                checked={isActive}
                onChange={(e) => setIsActive(e.target.checked)}
                disabled={pending}
                className="size-4 rounded border-app-border accent-app-emerald"
              />
              Lote activo
              <span className="text-[12px] text-app-ink-3">
                (los inactivos no aparecen al cambiar de lote)
              </span>
            </label>
          )}

          {error && (
            <p
              role="alert"
              className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-[13px] text-red-700"
            >
              {error}
            </p>
          )}

          <DialogFooter>
            <DialogClose asChild>
              <button
                type="button"
                disabled={pending}
                className="inline-flex h-10 items-center justify-center rounded-xl border border-app-border bg-app-surface px-4 font-medium text-[14px] text-app-ink-1 transition-colors hover:bg-app-surface-2 disabled:opacity-50"
              >
                Cancelar
              </button>
            </DialogClose>
            <button
              type="submit"
              disabled={pending}
              className="inline-flex h-10 items-center justify-center rounded-xl bg-app-ink-1 px-4 font-medium text-[14px] text-white transition-colors hover:bg-[#1e211d] disabled:opacity-50"
            >
              {pending ? "Guardando…" : submitLabel}
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
